import {
  CHECK_ID_MAX,
  CHECK_ID_PATTERN,
  DRILL_ITEM_ID_MAX,
  DRILL_ITEM_ID_PATTERN,
  DRILL_KINDS,
  LEVEL_ID_PATTERN,
  LESSON_ID_MAX,
  LESSON_ID_PATTERN,
  PROGRESS_KEY_MAX,
  SHOP_LINE_KEY_MAX,
  STEP_KINDS,
  TEMPLATE_ID_MAX,
  TEMPLATE_ID_PATTERN,
  isMw12Paper,
  isShopLineKey,
  parseProgressKey,
  validateMockWeek,
} from "@proef/domain";
import { z } from "zod";

/**
 * The Academy's wire contract (ticket 217): progress synced per device, the
 * drills due, the feature flag and the identity the edge issues. The key
 * grammar comes from `@proef/domain`, so a key the app calls well-formed is
 * well-formed here too. Ownership is the `X-Device-Id` header and nothing else:
 * no body ever names a device.
 */

// ---- identifiers -----------------------------------------------------------------

export const LessonId = z.string().max(LESSON_ID_MAX).regex(LESSON_ID_PATTERN);
export const CheckId = z.string().max(CHECK_ID_MAX).regex(CHECK_ID_PATTERN);
export const DrillItemId = z.string().max(DRILL_ITEM_ID_MAX).regex(DRILL_ITEM_ID_PATTERN);
export const TemplateId = z.string().max(TEMPLATE_ID_MAX).regex(TEMPLATE_ID_PATTERN);
export const LevelId = z.string().regex(LEVEL_ID_PATTERN);
/** An authored step id: stable across reorders, so progress survives an edit of the lesson. */
export const StepId = z.number().int().min(1).max(99);
/** A wine slot of a comparative step, `0…5` (a comparison holds two to six). */
export const ArchetypeIndex = z.number().int().min(0).max(5);

export type LessonId = z.infer<typeof LessonId>;
export type CheckId = z.infer<typeof CheckId>;
export type DrillItemId = z.infer<typeof DrillItemId>;
export type TemplateId = z.infer<typeof TemplateId>;
export type StepId = z.infer<typeof StepId>;

/**
 * `step:<lessonId>:<stepId>`, `check:<checkId>`, `drill:<itemId>`, `level:<levelId>` or
 * `shop:<line key>`, at most 128 characters.
 */
export const ProgressKey = z
  .string()
  .max(PROGRESS_KEY_MAX)
  .refine((key) => parseProgressKey(key) !== undefined, {
    error: "the key does not follow the progress key grammar",
  });
export type ProgressKey = z.infer<typeof ProgressKey>;

// ---- values ----------------------------------------------------------------------

const Instant = z.iso.datetime();

/** Values are loose objects: a newer app's extra fields are stored, bounded by the 4 KiB cap. */
export const StepValue = z.looseObject({
  kind: z.enum(STEP_KINDS),
  done: z.boolean(),
  /** First-done, never overwritten, so completion times stay stable across devices. */
  doneAt: Instant.exactOptional(),
  deferredAt: Instant.exactOptional(),
  /**
   * Ids only, never note content: the entries are strict so a field that smuggles a
   * note's words in is refused rather than stored. One entry per archetype, the later replaces the earlier.
   */
  notes: z
    .array(
      z.strictObject({
        archetype: ArchetypeIndex,
        noteId: z.string().min(1).max(64),
        flightId: z.string().min(1).max(64).exactOptional(),
      }),
    )
    .max(6)
    .exactOptional(),
  /** The device-shuffled reference labels of a drill step, so a reload keeps the same letters. */
  prep: z
    .record(z.string().min(1).max(8), DrillItemId)
    .refine((prep) => Object.keys(prep).length <= 12, { error: "at most 12 reference labels" })
    .exactOptional(),
});
export type StepValue = z.infer<typeof StepValue>;

export const CheckValue = z.looseObject({
  /** The authored option index, independent of any on-device shuffling. */
  answer: z.number().int().min(0).max(3),
  /** The latest answer decides: right then wrong counts as incorrect. */
  correct: z.boolean(),
  answeredAt: Instant,
  attempts: z.number().int().min(1),
  /** Answers sharing a sitting id derive a prove-it pass or fail on either side. */
  sitting: z.looseObject({ id: z.uuid(), startedAt: Instant }).exactOptional(),
});
export type CheckValue = z.infer<typeof CheckValue>;

export const DrillValue = z.looseObject({
  kind: z.enum(DRILL_KINDS),
  /** A UTC calendar day: due dates are days throughout. */
  due: z.iso.date(),
  firstAnsweredAt: Instant.exactOptional(),
  lastAnsweredAt: Instant.exactOptional(),
  lastCorrect: z.boolean().exactOptional(),
  /** Correct answers in a row; two or more is "secure". */
  streak: z.number().int().min(0),
  reps: z.number().int().min(0),
  lapses: z.number().int().min(0),
  /** The spaced-repetition library's card: opaque on the wire, validated and clamped by the domain. */
  state: z.record(z.string(), z.unknown()),
});
export type DrillValue = z.infer<typeof DrillValue>;

/** `level:<levelId>`: written once per fact, merged like any item. */
export const LevelValue = z
  .looseObject({
    completedAt: Instant.exactOptional(),
    passedAt: Instant.exactOptional(),
  })
  .refine((level) => level.completedAt !== undefined || level.passedAt !== undefined, {
    error: "a level record carries completedAt or passedAt",
  });
export type LevelValue = z.infer<typeof LevelValue>;

/** A line key of the shopping list: `a.<grape>.<style>.<country>.<region>.<gap>` or `g.<glass>`. */
export const ShopLineKey = z
  .string()
  .max(SHOP_LINE_KEY_MAX)
  .refine(isShopLineKey, { error: "the key does not follow the shopping line grammar" });
export type ShopLineKey = z.infer<typeof ShopLineKey>;

/**
 * `shop:<line key>`: one boolean, "have", with the time it was set. Strict, unlike the other
 * kinds: nothing a line could say about a wine has a place to travel in.
 */
export const ShopValue = z.strictObject({ have: z.boolean(), at: Instant });
export type ShopValue = z.infer<typeof ShopValue>;

/** `track:<id>`: the one fact of an enrolment, with the time it happened. Strict: no text has a place in it. */
export const TrackValue = z.strictObject({ enrolledAt: Instant });
export type TrackValue = z.infer<typeof TrackValue>;

/** `badge:<id>:<weekId>`: a mock-week badge, with the time it was earned. */
export const BadgeValue = z.strictObject({ awardedAt: Instant });
export type BadgeValue = z.infer<typeof BadgeValue>;

/** `mockweek:<weekId>`: the opt-in schedule of three papers on three days, `moved` once its remaining days were moved. */
export const MockWeekValue = z
  .strictObject({
    days: z
      .array(
        z.strictObject({
          day: z.iso.date(),
          paperId: z.string().min(1).max(32),
        }),
      )
      .length(3),
    reminderAt: z
      .string()
      .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/)
      .exactOptional(),
    createdAt: Instant,
    cancelledAt: Instant.exactOptional(),
    moved: z.literal(true).exactOptional(),
  })
  .refine((week) => validateMockWeek({ weekId: "", ...week }, isMw12Paper), {
    error:
      "a mock week is three different mw-12 papers on three consecutive days, once moved on three days",
  });
export type MockWeekValue = z.infer<typeof MockWeekValue>;

/** `research:<prompt>:<n>`: a submission as references and counts only; the body never leaves the device. */
export const ResearchValue = z
  .strictObject({
    submittedAt: Instant,
    wordCount: z.number().int().min(0).max(100_000),
    criteriaMet: z.number().int().min(0).max(16),
    criteriaTotal: z.number().int().min(0).max(16),
  })
  .refine((value) => value.criteriaMet <= value.criteriaTotal, {
    error: "no more criteria met than there are",
  });
export type ResearchValue = z.infer<typeof ResearchValue>;

const VALUE_BY_KIND = {
  step: StepValue,
  check: CheckValue,
  drill: DrillValue,
  level: LevelValue,
  shop: ShopValue,
  track: TrackValue,
  badge: BadgeValue,
  mockweek: MockWeekValue,
  research: ResearchValue,
} as const;

// ---- progress items --------------------------------------------------------------

/** The spec's 4 KiB is bytes, not characters. */
export const PROGRESS_ITEM_VALUE_MAX_BYTES = 4096;
export const PROGRESS_MAX_ITEMS = 500;
/** Shopping ticks in one PUT: a level holds a few dozen lines, so 200 is far past any real batch. */
export const SHOP_ITEMS_PER_PUT_MAX = 200;
const SHOP_PREFIX = "shop:";
const shopItemCount = (items: readonly { readonly key: string }[]): number =>
  items.filter((item) => item.key.startsWith(SHOP_PREFIX)).length;
/** A PUT body above this is refused (413) before it is parsed. */
export const PROGRESS_MAX_BODY_BYTES = 256 * 1024;

/** Size of `text` in UTF-8 bytes: what the 4 KiB limit and the database's own check measure. */
export function utf8ByteLength(text: string): number {
  let bytes = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
}

function valueBytes(value: unknown): number | undefined {
  try {
    const json = JSON.stringify(value);
    return json === undefined ? undefined : utf8ByteLength(json);
  } catch {
    return undefined;
  }
}

/**
 * One synced record. The envelope is strict (an unknown field such as `deviceId`
 * is refused because ownership is the header alone) and the value is chosen by
 * the key's prefix. `updatedAt` is the clock last-writer-wins compares, as
 * epoch milliseconds, so `…:00Z` and `…:00.000Z` are the same instant.
 */
export const ProgressItem = z
  .strictObject({
    key: ProgressKey,
    updatedAt: Instant,
    value: z.unknown(),
  })
  .superRefine((item, ctx) => {
    const bytes = valueBytes(item.value);
    if (bytes === undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["value"],
        message: "the value is missing or is not JSON",
      });
      return;
    }
    if (bytes > PROGRESS_ITEM_VALUE_MAX_BYTES) {
      ctx.addIssue({
        code: "custom",
        path: ["value"],
        message: `the value is larger than ${String(PROGRESS_ITEM_VALUE_MAX_BYTES)} bytes`,
      });
      return;
    }
    const key = parseProgressKey(item.key);
    if (key === undefined) return;
    const result = VALUE_BY_KIND[key.kind].safeParse(item.value);
    if (!result.success) {
      const where = result.error.issues[0]?.path.join(".");
      ctx.addIssue({
        code: "custom",
        path: ["value"],
        message: `the value is not a valid ${key.kind} record${where ? ` (${where})` : ""}`,
      });
    }
  });
export type ProgressItem = z.infer<typeof ProgressItem>;

const hasUniqueKeys = (items: readonly { readonly key: string }[]): boolean =>
  new Set(items.map((item) => item.key)).size === items.length;

/** The body of `PUT /v1/learn/progress`: one to 500 items, each key at most once. */
export const PutProgressRequest = z
  .strictObject({ items: z.array(ProgressItem).min(1).max(PROGRESS_MAX_ITEMS) })
  .refine((request) => hasUniqueKeys(request.items), {
    error: "a key may appear only once in a request",
  })
  .refine((request) => shopItemCount(request.items) <= SHOP_ITEMS_PER_PUT_MAX, {
    error: `at most ${String(SHOP_ITEMS_PER_PUT_MAX)} shopping ticks in one request`,
  });
export type PutProgressRequest = z.infer<typeof PutProgressRequest>;

/** `ApiError.details` of a refused PUT: every refused item, so the client sets exactly those aside. */
export const LearnPutRefused = z.object({
  refused: z.array(z.object({ key: z.string().max(PROGRESS_KEY_MAX), reason: z.string() })),
});
export type LearnPutRefused = z.infer<typeof LearnPutRefused>;

export type PutProgressVerdict =
  | { readonly ok: true; readonly request: PutProgressRequest }
  | {
      readonly ok: false;
      readonly message: string;
      /** Empty when the failure belongs to no single item (invalid shape, too many items). */
      readonly refused: LearnPutRefused["refused"];
    };

const PutProgressEnvelope = z.strictObject({
  items: z.array(z.unknown()).min(1).max(PROGRESS_MAX_ITEMS),
});

function refusalReason(error: z.ZodError): string {
  const first = error.issues[0];
  if (first === undefined) return "the item is not valid";
  switch (first.path[0]) {
    case "key":
      return "the key does not follow the progress key grammar";
    case "updatedAt":
      return "updatedAt must be an ISO 8601 UTC instant such as 2026-10-05T18:00:00Z";
    case "value":
      return first.message;
    default:
      return "an item holds exactly a key, an updatedAt and a value";
  }
}

function echoKey(raw: unknown): string {
  const key = typeof raw === "object" && raw !== null && "key" in raw ? raw.key : undefined;
  return typeof key === "string" ? key.slice(0, PROGRESS_KEY_MAX) : "";
}

/**
 * Parses a PUT body and names every refused item at once, in our words. The
 * route answers 400 with `details = { refused }`; the app drops those keys from
 * its chunk and retries the rest, so it never has to bisect. Agrees with
 * {@link PutProgressRequest} on what is acceptable.
 */
export function validatePutProgress(body: unknown): PutProgressVerdict {
  const envelope = PutProgressEnvelope.safeParse(body);
  if (!envelope.success) {
    return {
      ok: false,
      message: `the body must be {"items": [1 to ${String(PROGRESS_MAX_ITEMS)} items]} and nothing else; ownership is the X-Device-Id header alone`,
      refused: [],
    };
  }
  const refused: { key: string; reason: string }[] = [];
  const items: ProgressItem[] = [];
  const seen = new Set<string>();
  const reportedDuplicates = new Set<string>();
  for (const raw of envelope.data.items) {
    const parsed = ProgressItem.safeParse(raw);
    if (!parsed.success) {
      refused.push({ key: echoKey(raw), reason: refusalReason(parsed.error) });
      continue;
    }
    if (seen.has(parsed.data.key)) {
      if (!reportedDuplicates.has(parsed.data.key)) {
        reportedDuplicates.add(parsed.data.key);
        refused.push({
          key: parsed.data.key,
          reason: "the key appears more than once in this request",
        });
      }
      continue;
    }
    seen.add(parsed.data.key);
    items.push(parsed.data);
  }
  if (refused.length > 0) {
    return {
      ok: false,
      message: "some items were refused; the details name each one",
      refused,
    };
  }
  if (shopItemCount(items) > SHOP_ITEMS_PER_PUT_MAX) {
    return {
      ok: false,
      message: `at most ${String(SHOP_ITEMS_PER_PUT_MAX)} shopping ticks in one request`,
      refused: [],
    };
  }
  return { ok: true, request: { items } };
}

/** Response of GET and PUT `/v1/learn/progress`: the device's full live set, so a merged response replaces the mirror. */
export const LearnProgress = z.object({
  items: z.array(ProgressItem),
  servedAt: Instant,
});
export type LearnProgress = z.infer<typeof LearnProgress>;

/**
 * Response of `POST /v1/learn/account/progress/claim`: the account's full live set after this
 * device's progress was brought in, and how many of the device's items were taken.
 */
export const ClaimLearnProgressResponse = z.object({
  items: z.array(ProgressItem),
  servedAt: Instant,
  claimed: z.number().int().min(0),
});
export type ClaimLearnProgressResponse = z.infer<typeof ClaimLearnProgressResponse>;

// ---- drills due ------------------------------------------------------------------

/** Response of `GET /v1/learn/drills/due`, computed by the same pure function the device runs. */
export const DrillsDue = z.object({
  items: z.array(
    z.object({
      itemId: DrillItemId,
      kind: z.enum(DRILL_KINDS),
      firstExposure: z.boolean(),
      lessonId: LessonId,
    }),
  ),
  /** Everything due before the cap, so the screen can say "12 of 20". */
  dueCount: z.number().int().min(0),
  /** Echoed so the client never hard-codes the product caps. */
  cap: z.object({ reviews: z.number().int().min(0), new: z.number().int().min(0) }),
  /** The instant the cap's UTC day was computed for. */
  servedAt: Instant,
});
export type DrillsDue = z.infer<typeof DrillsDue>;

// ---- export ----------------------------------------------------------------------

/** One ledger row: how many drill answers a device gave in an ISO week. */
export const LearnActCount = z.object({
  week: z.string().regex(/^\d{4}-W(?:0[1-9]|[1-4]\d|5[0-3])$/),
  kind: z.literal("drill"),
  count: z.number().int().min(1),
});
export type LearnActCount = z.infer<typeof LearnActCount>;

/** The learn section of `GET /v1/privacy/export`: every item, and the ledger as counts. */
export const LearnExport = z.object({
  items: z.array(ProgressItem),
  acts: z.array(LearnActCount),
  /** The depth's sittings (structured answers), reveals and calibration records; absent from an older export. */
  depth: z
    .object({
      sittings: z.array(z.object({ key: z.string(), value: z.unknown() })),
      reveals: z.array(z.object({ key: z.string(), value: z.unknown() })),
      calibration: z.array(z.object({ key: z.string(), value: z.unknown() })),
    })
    .optional(),
});
export type LearnExport = z.infer<typeof LearnExport>;

// ---- paused lessons and identity ---------------------------------------------------

/** The SDK flag whose payload carries the paused lessons; its on/off answer gates nothing. */
export const ACADEMY_PATH_FLAG_KEY = "academy-path";

/** The remote unpublished list; a payload that fails to parse is read as `{ unpublished: [] }` by the app. */
export const AcademyPathFlagPayload = z.object({ unpublished: z.array(LessonId).max(64) });
export type AcademyPathFlagPayload = z.infer<typeof AcademyPathFlagPayload>;

/** The analytics identity the edge issues per device: 32 lower-case hex characters. */
export const IdentityId = z.string().regex(/^[0-9a-f]{32}$/);
export type IdentityId = z.infer<typeof IdentityId>;

/** Response header carrying the identity; exposed to cross-origin script by CORS. */
export const IDENTITY_HEADER = "x-proef-identity";
/** `HttpOnly` cookie carrying the same value, the edge's own continuity. */
export const IDENTITY_COOKIE = "proef_identity";
